import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaCar,
  FaSearch,
  FaTimes,
  FaTrash,
  FaCheckCircle,
  FaPlus,
  FaHeartbeat,
  FaArrowLeft,
  FaIdCard,
  FaListUl,
} from 'react-icons/fa';
import { vehiclePickerApi } from '../../services/vehiclePickerApi';
import { discoveryService } from '../../services/apiService';
import { maskRegistration } from '../../utils/displayName';
import { useAuth } from '../../auth/AuthContext';
import {
  getVehicleImageUrl,
  VEHICLE_CARD_IMAGE_PLACEHOLDER,
  stripDuplicateMakeFromModel,
} from '../../data/vehicleData';

const FUEL_TYPES = ['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'];
const MAX_VEHICLES = 20;

const normalizeRegInput = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const vehicleCardClass =
  'w-full min-w-0 shrink-0 snap-start sm:snap-align-none sm:shrink sm:w-full';

/**
 * B-02 VehiclePicker — Add & Manage Vehicles
 */
const VehiclePicker = ({ mode = 'onboarding', overlay = false, onClose }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [activeTab, setActiveTab] = useState('registration');
  const [banner, setBanner] = useState('');
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const [regNumber, setRegNumber] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [variants, setVariants] = useState([]);
  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [generationId, setGenerationId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [manualReg, setManualReg] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(true);

  const hasSavedVehicles = savedVehicles.length > 0;

  useEffect(() => {
    if (!loadingList) setShowAddForm(savedVehicles.length === 0);
  }, [loadingList, savedVehicles.length]);

  const loadVehicles = useCallback(async () => {
    if (!isAuthenticated) {
      setSavedVehicles([]);
      setLoadingList(false);
      return;
    }
    try {
      setLoadingList(true);
      const list = await vehiclePickerApi.getVehicles();
      setSavedVehicles(Array.isArray(list) ? list : []);
    } catch {
      setSavedVehicles([]);
    } finally {
      setLoadingList(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadVehicles();
    vehiclePickerApi.listMakes().then(setMakes).catch(() => setMakes([]));
  }, [loadVehicles]);

  useEffect(() => {
    if (!makeId) {
      setModels([]);
      setModelId('');
      return;
    }
    vehiclePickerApi.listModels(makeId).then(setModels).catch(() => setModels([]));
    setModelId('');
    setGenerationId('');
    setVariantId('');
    setGenerations([]);
    setVariants([]);
  }, [makeId]);

  useEffect(() => {
    if (!modelId) {
      setGenerations([]);
      setGenerationId('');
      return;
    }
    vehiclePickerApi.listGenerations(modelId).then(setGenerations).catch(() => setGenerations([]));
    setGenerationId('');
    setVariantId('');
    setVariants([]);
  }, [modelId]);

  useEffect(() => {
    if (!generationId) {
      setVariants([]);
      setVariantId('');
      return;
    }
    vehiclePickerApi.listVariants(generationId).then(setVariants).catch(() => setVariants([]));
    setVariantId('');
  }, [generationId]);

  useEffect(() => {
    if (!variantId) return;
    const v = variants.find((x) => String(x._id || x.id) === String(variantId));
    if (v?.fuelType && !fuelType) setFuelType(v.fuelType);
  }, [variantId, variants, fuelType]);

  const handleFindMyCar = async () => {
    if (!isAuthenticated) {
      toast.info('Please log in to use registration lookup');
      navigate('/login-phone');
      return;
    }
    const reg = normalizeRegInput(regNumber);
    if (!reg) {
      toast.warning('Enter a registration number');
      return;
    }
    setLookupLoading(true);
    setPrefill(null);
    setBanner('');
    try {
      const result = await discoveryService.lookupRegistration(reg);
      const data = result?.data || result;
      const vehicle = data?.lookup?.vehicle || data?.vehicle;
      const meta = data?.lookup?.meta || data?.meta;
      if (meta?.banner) setBanner(meta.banner);
      if (vehicle && (vehicle.make || vehicle.model)) {
        setPrefill({ ...vehicle, registrationNumber: reg });
        if (data?.needsVariantConfirm && !vehicle.variantId) {
          setBanner('Multiple variants found — confirm variant below or use manual entry.');
        }
      } else {
        setActiveTab('manual');
        setBanner(meta?.banner || 'Could not auto-detect. Please select manually.');
      }
    } catch (err) {
      setActiveTab('manual');
      setBanner('Could not auto-detect. Please select manually.');
      toast.error(err?.response?.data?.message || 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleConfirmPrefill = async () => {
    if (!prefill) return;
    if (savedVehicles.length >= MAX_VEHICLES) {
      toast.error('Maximum of 20 vehicles per account');
      return;
    }
    if (!prefill.generationId || !prefill.variantId) {
      toast.warning('Could not match catalogue — please use manual entry');
      setActiveTab('manual');
      return;
    }
    setSaving(true);
    try {
      const saved = await discoveryService.lookupRegistration(prefill.registrationNumber, {
        save: true,
        generationId: prefill.generationId,
        variantId: prefill.variantId,
        fuelType: prefill.fuelType || 'Petrol',
      });
      const vehicle = saved?.data?.savedVehicle || saved?.savedVehicle;
      if (!vehicle) {
        await vehiclePickerApi.addVehicle({
          registrationNumber: prefill.registrationNumber,
          make: prefill.make,
          model: prefill.model,
          generationId: prefill.generationId,
          variantId: prefill.variantId,
          fuelType: prefill.fuelType || 'Petrol',
        });
      }
      toast.success('Vehicle saved!');
      setPrefill(null);
      setRegNumber('');
      setBanner('');
      await loadVehicles();
      setShowAddForm(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error('This vehicle is already in your list.');
      else toast.error(err?.response?.data?.message || 'Could not save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleManualSave = async () => {
    if (!isAuthenticated) {
      toast.info('Please log in to save a vehicle');
      navigate('/login-phone');
      return;
    }
    const errors = {};
    if (!makeId) errors.makeId = true;
    if (!modelId) errors.modelId = true;
    if (!generationId) errors.generationId = true;
    if (!variantId) errors.variantId = true;
    if (!fuelType) errors.fuelType = true;
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      toast.warning('Please complete all required fields');
      return;
    }
    if (savedVehicles.length >= MAX_VEHICLES) {
      toast.error('Maximum of 20 vehicles per account');
      return;
    }
    const makeName = makes.find((m) => String(m._id || m.id) === String(makeId))?.name || '';
    const modelName = models.find((m) => String(m._id || m.id) === String(modelId))?.name || '';
    setSaving(true);
    try {
      await vehiclePickerApi.addVehicle({
        registrationNumber: manualReg ? normalizeRegInput(manualReg) : undefined,
        make: makeName,
        model: modelName,
        generationId,
        variantId,
        fuelType,
      });
      toast.success('Vehicle saved!');
      setMakeId('');
      setModelId('');
      setGenerationId('');
      setVariantId('');
      setFuelType('');
      setManualReg('');
      setFieldErrors({});
      await loadVehicles();
      setShowAddForm(false);
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error('This vehicle is already in your list.');
      else toast.error(err?.response?.data?.message || 'Could not save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (vehicle) => {
    const id = vehicle._id || vehicle.id;
    try {
      await vehiclePickerApi.setActive(id);
      await loadVehicles();
      toast.success('Active vehicle updated');
    } catch {
      toast.error('Failed to set active vehicle');
    }
  };

  const handleFindParts = async (vehicle) => {
    const id = vehicle._id || vehicle.id;
    try {
      await vehiclePickerApi.setActive(id);
    } catch { /* continue */ }
    const params = new URLSearchParams({
      vehicleMake: vehicle.make,
      vehicleModel: vehicle.model,
      vehicleYear: String(vehicle.year || ''),
      vehicleVariant: vehicle.variant || '',
      spareType: 'new',
      origin: 'OEM',
    });
    navigate(`/catalog?${params.toString()}`);
  };

  const handleViewPassport = (vehicle) => {
    const id = vehicle._id || vehicle.id;
    navigate(id ? `/health-passport?vehicleId=${id}` : '/health-passport');
  };

  const confirmDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    if (!id) return;
    setDeleting(true);
    try {
      await vehiclePickerApi.deleteVehicle(id);
      toast.success('Vehicle deleted');
      setDeleteTarget(null);
      await loadVehicles();
    } catch {
      toast.error('Failed to delete vehicle');
    } finally {
      setDeleting(false);
    }
  };

  const isActive = (v) => v.isDefault || v.isActive;

  const vehicleImageSrc = (vehicle) => {
    if (vehicle.photo && String(vehicle.photo).trim()) return String(vehicle.photo).trim();
    const model = stripDuplicateMakeFromModel(vehicle.make, vehicle.model);
    return getVehicleImageUrl(vehicle.make, model) || VEHICLE_CARD_IMAGE_PLACEHOLDER;
  };

  const selectClass = (err, disabled) =>
    `w-full px-4 py-3 rounded-xl border text-sm bg-white transition-all focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none ${
      err ? 'border-red-400 bg-red-50' : 'border-gray-200'
    } ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}`;

  const openAddForm = () => {
    setShowAddForm(true);
    setActiveTab('registration');
    setBanner('');
  };

  const pageContent = (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 pb-28 lg:pb-12">
      {/* Page header */}
      <div className="mb-8 sm:mb-10">
        {!overlay && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-800 mb-4 transition-colors"
          >
            <FaArrowLeft className="text-xs" /> Back
          </button>
        )}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-red-500 mb-2">My Garage</p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              {hasSavedVehicles ? (
                <>Your <span className="text-red-500">Vehicles</span></>
              ) : (
                <>Add your <span className="text-red-500">Vehicle</span></>
              )}
            </h1>
            <p className="text-sm text-gray-500 mt-2 max-w-xl">
              {hasSavedVehicles
                ? 'Select a vehicle for parts · manage fitment · open health passport'
                : 'Add your car to get exact-fit parts and personalized recommendations'}
            </p>
          </div>
          {hasSavedVehicles && (
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-3 py-1.5 rounded-full">
                {savedVehicles.length} / {MAX_VEHICLES} saved
              </span>
              {mode === 'onboarding' && (
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
                >
                  Continue to Home
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {!isAuthenticated && (
        <div className="mb-8 px-5 py-4 rounded-2xl bg-blue-50 border border-blue-100 text-blue-900 text-sm flex flex-wrap items-center justify-between gap-3">
          <span>Log in to save vehicles and use registration lookup.</span>
          <Link
            to="/login-phone"
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            Log in
          </Link>
        </div>
      )}

      {/* Saved vehicles — matches home SearchSection card style */}
      {isAuthenticated && (
        <section className="mb-10">
          {loadingList ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-72 rounded-2xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {savedVehicles.length > 0 && (
                <p className="text-[11px] text-gray-400 mb-3 sm:hidden">Swipe sideways to see all vehicles →</p>
              )}
              <div
                className="
                  flex gap-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory
                  pb-3 -mx-4 px-4
                  sm:mx-0 sm:px-0 sm:pb-0 sm:overflow-visible sm:snap-none
                  sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 sm:gap-4 md:gap-5
                  w-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
                "
              >
                {savedVehicles.map((vehicle, index) => {
                  const active = isActive(vehicle);
                  const id = vehicle._id || vehicle.id;
                  const masked = maskRegistration(vehicle.registrationNumber);
                  const modelLabel = stripDuplicateMakeFromModel(vehicle.make, vehicle.model);
                  return (
                    <motion.div
                      key={id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={`${vehicleCardClass} w-[min(88vw,340px)] sm:w-full bg-white rounded-2xl overflow-hidden transition-all duration-300 group flex flex-col min-h-[280px] ${
                        active
                          ? 'border-2 border-blue-500 ring-4 ring-blue-500/10 shadow-md'
                          : 'border border-gray-100 shadow-sm hover:shadow-md'
                      }`}
                    >
                      <div className="relative h-32 sm:h-36 bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-2">
                        <img
                          loading="lazy"
                          src={vehicleImageSrc(vehicle)}
                          alt={`${vehicle.make} ${modelLabel}`}
                          className="relative z-10 max-h-full max-w-full object-contain transform group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = VEHICLE_CARD_IMAGE_PLACEHOLDER;
                          }}
                        />
                        <span className="absolute top-2 left-2 z-20 px-2 py-0.5 rounded-full text-[9px] font-bold bg-blue-500 text-white">
                          CAR
                        </span>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(vehicle)}
                          className="absolute top-2 right-2 z-20 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-md opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                          title="Delete"
                        >
                          <FaTrash className="text-[10px]" />
                        </button>
                        {active && (
                          <div className="absolute bottom-2 right-2 z-20">
                            <FaCheckCircle className="text-green-500 text-xl drop-shadow" />
                          </div>
                        )}
                      </div>

                      <div className="p-4 flex flex-col flex-1 border-t border-gray-50">
                        <h3 className="text-sm font-bold text-gray-900 line-clamp-1">
                          {vehicle.make} {modelLabel}
                        </h3>
                        {vehicle.variant && (
                          <p className="text-xs font-medium text-blue-600 truncate mt-0.5">{vehicle.variant}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Year <span className="font-semibold text-gray-800">{vehicle.year}</span>
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {vehicle.fuelType && (
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                              {vehicle.fuelType}
                            </span>
                          )}
                          {masked && (
                            <span className="text-[10px] font-mono text-gray-400">{masked}</span>
                          )}
                        </div>

                        <div className="mt-auto pt-3 space-y-2">
                          <button
                            type="button"
                            onClick={() => handleViewPassport(vehicle)}
                            className="w-full flex items-center justify-center gap-1.5 rounded-xl bg-slate-800 px-3 py-2.5 text-xs font-semibold text-white hover:bg-slate-700 transition-colors"
                          >
                            <FaHeartbeat className="text-[10px]" />
                            Health Passport
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            {!active ? (
                              <button
                                type="button"
                                onClick={() => handleSetActive(vehicle)}
                                className="text-xs font-semibold px-2 py-2 rounded-xl border border-blue-200 text-blue-700 hover:bg-blue-50"
                              >
                                Set Active
                              </button>
                            ) : (
                              <span className="text-xs font-bold text-center py-2 text-blue-600 uppercase tracking-wide">
                                Active
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleFindParts(vehicle)}
                              className="text-xs font-semibold px-2 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600"
                            >
                              Find Parts
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}

                {/* Add vehicle card */}
                {!showAddForm && savedVehicles.length < MAX_VEHICLES && (
                  <button
                    type="button"
                    onClick={openAddForm}
                    className={`${vehicleCardClass} w-[min(88vw,340px)] sm:w-full min-h-[280px] rounded-2xl border-2 border-dashed border-gray-200 bg-white/80 hover:border-blue-300 hover:bg-blue-50/40 transition-all flex flex-col items-center justify-center p-6 text-center group`}
                  >
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg mb-3 group-hover:scale-105 transition-transform">
                      <FaPlus className="text-white text-xl" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">Add vehicle</h3>
                    <p className="mt-1 text-xs text-gray-500 max-w-[200px] leading-relaxed">
                      Registration lookup or manual entry
                    </p>
                  </button>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {/* Add vehicle form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 shadow-lg overflow-hidden"
          >
            <div className="px-5 sm:px-8 py-5 sm:py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {hasSavedVehicles ? 'Add another vehicle' : 'Add your vehicle'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Registration lookup or manual catalogue entry</p>
              </div>
              {hasSavedVehicles && (
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="text-sm text-gray-500 hover:text-gray-800 font-medium"
                >
                  Cancel
                </button>
              )}
            </div>

            <div className="p-5 sm:p-8">
              {banner && (
                <div className="mb-6 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-2">
                  <span className="shrink-0 mt-0.5">⚠</span>
                  <span>{banner}</span>
                </div>
              )}

              {/* Pill tabs */}
              <div className="inline-flex p-1 rounded-xl bg-gray-100 mb-8 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => { setActiveTab('registration'); setBanner(''); }}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    activeTab === 'registration'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FaIdCard className="text-xs" />
                  Registration
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('manual')}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    activeTab === 'manual'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FaListUl className="text-xs" />
                  Manual entry
                </button>
              </div>

              {activeTab === 'registration' && (
                <div className="max-w-2xl">
                  <label className="block text-sm font-semibold text-gray-800 mb-3">
                    Indian registration number
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex flex-1 items-stretch rounded-xl border-2 border-gray-200 bg-white overflow-hidden focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
                      <span className="flex items-center px-4 bg-blue-600 text-white text-xs font-bold tracking-wider shrink-0">
                        IND
                      </span>
                      <input
                        type="text"
                        value={regNumber}
                        onChange={(e) => setRegNumber(normalizeRegInput(e.target.value))}
                        onKeyDown={(e) => e.key === 'Enter' && handleFindMyCar()}
                        placeholder="DL01AB1234"
                        maxLength={12}
                        className="flex-1 px-4 py-3.5 uppercase tracking-widest font-mono text-gray-900 outline-none bg-transparent text-lg"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleFindMyCar}
                      disabled={lookupLoading || !regNumber}
                      className="px-6 py-3.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2 shrink-0 shadow-md shadow-red-500/20"
                    >
                      {lookupLoading ? (
                        <span className="animate-pulse">Finding…</span>
                      ) : (
                        <>
                          <FaSearch /> Find My Car
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Example: DL01AB1234 · uppercase, no spaces</p>

                  <AnimatePresence>
                    {prefill && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-8 rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6"
                      >
                        <div className="flex items-center gap-2 mb-4">
                          <FaCheckCircle className="text-green-500" />
                          <p className="text-sm font-bold text-blue-800">We found your car</p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-6">
                          {[
                            ['Make', prefill.make],
                            ['Model', prefill.model],
                            ['Year', prefill.year || '—'],
                            ['Fuel', prefill.fuelType || '—'],
                            ['Variant', prefill.variant || '—'],
                          ].map(([label, val]) => (
                            <div key={label} className="bg-white/80 rounded-xl px-3 py-2 border border-blue-100">
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
                              <p className="font-bold text-gray-900 mt-0.5">{val}</p>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={handleConfirmPrefill}
                          disabled={saving}
                          className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-md"
                        >
                          <FaCheckCircle /> Confirm &amp; Save
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {activeTab === 'manual' && (
                <div className="max-w-3xl space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { key: 'makeId', label: 'Make', value: makeId, onChange: setMakeId, disabled: false, options: makes, optVal: (m) => m._id || m.id, optLabel: (m) => m.label || m.name },
                      { key: 'modelId', label: 'Model', value: modelId, onChange: setModelId, disabled: !makeId, options: models, optVal: (m) => m._id || m.id, optLabel: (m) => m.label || m.name },
                      { key: 'generationId', label: 'Generation', value: generationId, onChange: setGenerationId, disabled: !modelId, options: generations, optVal: (g) => g._id || g.id, optLabel: (g) => g.label },
                      { key: 'variantId', label: 'Variant', value: variantId, onChange: setVariantId, disabled: !generationId, options: variants, optVal: (v) => v._id || v.id, optLabel: (v) => v.label || v.variant },
                    ].map(({ key, label, value, onChange, disabled, options, optVal, optLabel }) => (
                      <div key={key}>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5">
                          {label} *
                        </label>
                        <select
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          disabled={disabled}
                          className={selectClass(fieldErrors[key], disabled)}
                        >
                          <option value="">Select {label}</option>
                          {options.map((item) => (
                            <option key={optVal(item)} value={optVal(item)}>
                              {optLabel(item)}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
                      Fuel type *
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {FUEL_TYPES.map((f) => (
                        <button
                          key={f}
                          type="button"
                          disabled={!variantId}
                          onClick={() => setFuelType(f)}
                          className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                            fuelType === f
                              ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 disabled:opacity-40'
                          } ${fieldErrors.fuelType && !fuelType ? 'ring-2 ring-red-300' : ''}`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5">
                      Registration (optional)
                    </label>
                    <input
                      type="text"
                      value={manualReg}
                      onChange={(e) => setManualReg(normalizeRegInput(e.target.value))}
                      placeholder="For identification only — no Vahan lookup"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 uppercase font-mono text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleManualSave}
                    disabled={saving}
                    className="w-full sm:w-auto min-w-[200px] py-3.5 px-8 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 disabled:opacity-50 shadow-md shadow-red-500/20"
                  >
                    {saving ? 'Saving…' : 'Save Vehicle'}
                  </button>
                </div>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );

  if (overlay) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="checkout-light-page relative bg-white w-full sm:max-w-4xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl"
        >
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
              aria-label="Close"
            >
              <FaTimes />
            </button>
          )}
          {pageContent}
        </motion.div>

        <AnimatePresence>
          {deleteTarget && (
            <DeleteModal
              vehicle={deleteTarget}
              deleting={deleting}
              onCancel={() => setDeleteTarget(null)}
              onConfirm={confirmDelete}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="checkout-light-page w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-slate-50">
      {pageContent}

      <AnimatePresence>
        {deleteTarget && (
          <DeleteModal
            vehicle={deleteTarget}
            deleting={deleting}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={confirmDelete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

function DeleteModal({ vehicle, deleting, onCancel, onConfirm }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <FaTrash className="text-red-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-1">Delete vehicle?</h3>
        <p className="text-gray-800 font-semibold">
          {vehicle.make} {vehicle.model} · {vehicle.year}
        </p>
        <p className="text-sm text-gray-500 mt-2 mb-6">This cannot be undone.</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default VehiclePicker;
